'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getWeekData, PHASE_META } from '@/lib/curriculum';
import {
  getWeekProgress,
  toggleDay,
  toggleAssessment,
  getNote,
  setNote,
  getResources,
  setResources,
  WeekProgress,
  Resource,
} from '@/lib/storage';
import { getCurrentWeek } from '@/lib/storage';
import DayTabs from './DayTabs';
import DayPanel from './DayPanel';
import AssessmentPanel from './AssessmentPanel';
import ResourcePanel from './ResourcePanel';
import PomodoroTimer from './PomodoroTimer';

export default function WeekDetailClient() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [mounted, setMounted] = useState(false);
  const [weekNum, setWeekNum] = useState(1);
  const [activeDay, setActiveDay] = useState(0);
  const [weekProgress, setWeekProgress] = useState<WeekProgress>({
    daysComplete: [false, false, false, false, false],
    assessmentPassed: false,
    startedAt: null,
    completedAt: null,
  });
  const [notes, setNotes] = useState<string[]>(['', '', '', '', '']);
  const [resources, setResourcesState] = useState<Resource[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Determine week number from query param or current week
  useEffect(() => {
    const wParam = searchParams.get('week');
    const n = wParam ? parseInt(wParam, 10) : getCurrentWeek();
    const clamped = Math.min(Math.max(n, 1), 80);
    setWeekNum(clamped);

    // Set today's day as default tab
    const today = new Date().getDay();
    const defaultDay = today >= 1 && today <= 5 ? today - 1 : 0;
    setActiveDay(defaultDay);

    setMounted(true);
  }, [searchParams]);

  // Load data when weekNum changes
  useEffect(() => {
    if (!mounted) return;
    const wp = getWeekProgress(weekNum);
    setWeekProgress(wp);
    const loadedNotes = [0, 1, 2, 3, 4].map(i => getNote(weekNum, i));
    setNotes(loadedNotes);
    setResourcesState(getResources(weekNum));
  }, [weekNum, mounted]);

  const week = getWeekData(weekNum);
  const phase = week ? PHASE_META[week.phase] : null;

  const handleToggleDay = useCallback((dayIdx: number) => {
    const updated = toggleDay(weekNum, dayIdx);
    setWeekProgress(updated);
  }, [weekNum]);

  const handleToggleAssessment = useCallback(() => {
    const updated = toggleAssessment(weekNum);
    setWeekProgress(updated);
  }, [weekNum]);

  const handleNoteChange = useCallback((dayIdx: number, text: string) => {
    setNotes(prev => {
      const next = [...prev];
      next[dayIdx] = text;
      return next;
    });
    setSaveStatus('saving');
    // Debounced save
    const timer = setTimeout(() => {
      setNote(weekNum, dayIdx, text);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 600);
    return () => clearTimeout(timer);
  }, [weekNum]);

  const handleResourcesChange = useCallback((newResources: Resource[]) => {
    setResourcesState(newResources);
    setResources(weekNum, newResources);
  }, [weekNum]);

  const navigateWeek = (delta: number) => {
    const next = Math.min(Math.max(weekNum + delta, 1), 80);
    router.push(`/week-detail-view?week=${next}`);
  };

  if (!mounted || !week || !phase) {
    return (
      <div style={{ padding: '32px 24px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[1, 2, 3].map(i => (
            <div
              key={`detail-skel-${i}`}
              style={{
                height: i === 1 ? '80px' : '200px',
                borderRadius: '12px',
                backgroundColor: 'var(--bg-card)',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  const completedCount = weekProgress.daysComplete.filter(Boolean).length;
  const isFullyComplete = completedCount === 5 && weekProgress.assessmentPassed;

  return (
    <div
      style={{
        padding: '32px 24px',
        maxWidth: '900px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        paddingBottom: '120px', // room for Pomodoro
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: '11px',
                fontFamily: 'IBM Plex Mono, monospace',
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: '4px',
                backgroundColor: phase.color,
                color: phase.textColor,
                border: `1px solid ${phase.border}`,
                letterSpacing: '0.04em',
              }}
            >
              PHASE {week.phase} · {phase.label.toUpperCase()}
            </span>
            <span
              style={{
                fontSize: '11px',
                fontFamily: 'IBM Plex Mono, monospace',
                color: 'var(--text-muted)',
                padding: '3px 9px',
                borderRadius: '4px',
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
              }}
            >
              WEEK {weekNum} OF 80
            </span>
            {isFullyComplete && (
              <span
                style={{
                  fontSize: '11px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  color: 'var(--accent)',
                  padding: '3px 9px',
                  borderRadius: '4px',
                  backgroundColor: 'var(--accent-glow)',
                  border: '1px solid var(--accent-border)',
                }}
              >
                ✓ COMPLETE
              </span>
            )}
            {saveStatus === 'saving' && (
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace' }}>
                Saving…
              </span>
            )}
            {saveStatus === 'saved' && (
              <span style={{ fontSize: '11px', color: 'var(--accent)', fontFamily: 'IBM Plex Mono, monospace' }}>
                Saved ✓
              </span>
            )}
          </div>

          <h1
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              fontSize: '26px',
              fontWeight: 400,
              color: 'var(--text-primary)',
              lineHeight: 1.2,
            }}
          >
            {week.title}
          </h1>

          {/* Progress bar */}
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                backgroundColor: 'var(--bg-elevated)',
                overflow: 'hidden',
                maxWidth: '200px',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(completedCount / 5) * 100}%`,
                  backgroundColor: 'var(--accent)',
                  borderRadius: '2px',
                  transition: 'width 300ms ease',
                }}
              />
            </div>
            <span style={{ fontSize: '12px', fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-muted)' }}>
              {completedCount}/5 days
            </span>
          </div>
        </div>

        {/* Prev / Next navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={() => navigateWeek(-1)}
            disabled={weekNum <= 1}
            aria-label="Previous week"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-elevated)',
              color: weekNum <= 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
              cursor: weekNum <= 1 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              opacity: weekNum <= 1 ? 0.5 : 1,
              transition: 'all 150ms ease',
            }}
          >
            <ChevronLeft size={15} />
            Prev
          </button>
          <button
            onClick={() => navigateWeek(1)}
            disabled={weekNum >= 80}
            aria-label="Next week"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-elevated)',
              color: weekNum >= 80 ? 'var(--text-muted)' : 'var(--text-secondary)',
              cursor: weekNum >= 80 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              opacity: weekNum >= 80 ? 0.5 : 1,
              transition: 'all 150ms ease',
            }}
          >
            Next
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Day tabs */}
      <DayTabs
        activeDay={activeDay}
        onSelectDay={setActiveDay}
        daysComplete={weekProgress.daysComplete}
      />

      {/* Day panel */}
      <DayPanel
        weekNum={weekNum}
        dayIndex={activeDay}
        dayContent={week.days[activeDay]}
        isDayComplete={weekProgress.daysComplete[activeDay]}
        note={notes[activeDay]}
        onToggleDay={() => handleToggleDay(activeDay)}
        onNoteChange={text => handleNoteChange(activeDay, text)}
      />

      {/* Assessment */}
      <AssessmentPanel
        assessment={week.assessment}
        isPassed={weekProgress.assessmentPassed}
        onToggle={handleToggleAssessment}
      />

      {/* Resources */}
      <ResourcePanel
        resources={resources}
        onResourcesChange={handleResourcesChange}
      />

      {/* Pomodoro Timer (floating) */}
      <PomodoroTimer currentTopic={week.days[activeDay]?.topic ?? ''} />
    </div>
  );
}